import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { MapPin, WifiHigh, Clock, PhoneCall, ForkKnife, Buildings, Info, House, Copy, CheckCircle, VideoCamera, Door, IdentificationCard, UploadSimple } from "@phosphor-icons/react";

const TEMPLATES = {
  classic: { hero: "from-amber-500 to-amber-600",  accent: "text-amber-600",  accentBg: "bg-amber-50",  accentBorder: "border-amber-200",  dot: "text-amber-400" },
  minimal: { hero: "from-slate-700 to-slate-800",  accent: "text-slate-700",  accentBg: "bg-slate-50",  accentBorder: "border-slate-200",  dot: "text-slate-400" },
  luxury:  { hero: "from-yellow-900 to-yellow-950", accent: "text-yellow-600", accentBg: "bg-yellow-50", accentBorder: "border-yellow-200", dot: "text-yellow-500" },
  cozy:    { hero: "from-rose-500 to-rose-600",    accent: "text-rose-600",   accentBg: "bg-rose-50",   accentBorder: "border-rose-200",   dot: "text-rose-400" },
};

const BADGE_STYLES = {
  "Host's Pick": "bg-amber-100 text-amber-700 border-amber-200",
  "Must Try":    "bg-green-100 text-green-700 border-green-200",
  "Hidden Gem":  "bg-purple-100 text-purple-700 border-purple-200",
  "Local Fave":  "bg-blue-100 text-blue-700 border-blue-200",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Section({ icon: Icon, title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white text-left"
      >
        <span className="flex items-center gap-2.5 font-semibold text-slate-800 text-sm">
          <Icon size={18} className="text-amber-500" weight="duotone" />
          {title}
        </span>
        <span className="text-slate-400 text-lg leading-none">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 bg-white border-t border-slate-100">{children}</div>}
    </div>
  );
}

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: (clientX - r.left) * (c.width / r.width), y: (clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#1e293b";
    ctx.lineTo(x, y); ctx.stroke();
    dirty.current = true;
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(dirty.current ? canvasRef.current : null); } };
  const clear = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
    dirty.current = false; onChange(null);
  };

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden">
        <canvas
          ref={canvasRef} width={600} height={180}
          className="w-full touch-none" style={{ height: 120 }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] text-slate-400">Sign above with your finger or mouse</span>
        <button type="button" onClick={clear} className="text-[11px] text-slate-500 underline">Clear</button>
      </div>
    </div>
  );
}

function CheckinForm({ bookingId, guestName, details, terms, houseRules }) {
  const [name, setName] = useState(guestName || "");
  const [phone, setPhone] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [sigCanvas, setSigCanvas] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(!!details?.submitted);
  const [err, setErr] = useState("");

  if (done) return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2.5">
      <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />
      <p className="text-sm text-emerald-700 font-medium">Check-in complete. Your host has been notified — see you soon!</p>
    </div>
  );

  // Only ask for what the booking doesn't already have.
  const needPhone = !details?.phone_set;
  const needId    = !details?.id_set;      // direct bookings already have the ID
  const needSign  = !details?.signed;      // ask for signature + terms unless already signed

  const canvasToBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

  const submit = async () => {
    setErr("");
    if (!name.trim()) { setErr("Please enter your full name."); return; }
    if (needPhone && !phone.trim()) { setErr("Please enter your phone number."); return; }
    if (needId && !idFile) { setErr("A government ID is required before check-in."); return; }
    if (needSign && !sigCanvas) { setErr("Please add your signature."); return; }
    if (needSign && !agreed) { setErr("Please agree to the terms and house rules to check in."); return; }
    setSubmitting(true);
    try {
      let idUrl = "";
      if (idFile) {
        const fd = new FormData();
        fd.append("file", idFile);
        const up = await fetch(`${BACKEND_URL}/api/book/upload-id`, { method: "POST", body: fd });
        if (!up.ok) throw new Error("ID upload failed — try a smaller image (max 10 MB)");
        idUrl = (await up.json()).url || "";
      }
      let sigUrl = "";
      if (sigCanvas) {
        const blob = await canvasToBlob(sigCanvas);
        const fd = new FormData();
        fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
        const up = await fetch(`${BACKEND_URL}/api/book/upload-signature`, { method: "POST", body: fd });
        if (!up.ok) throw new Error("Signature upload failed — please try again");
        sigUrl = (await up.json()).url || "";
      }
      const res = await fetch(`${BACKEND_URL}/api/guide/${bookingId}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guest_name: name.trim(), guest_phone: phone.trim(),
          guest_id_url: idUrl, signature_url: sigUrl, terms_accepted: agreed,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Could not save your details");
      }
      setDone(true);
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-amber-400";
  return (
    <div className="rounded-2xl border-2 border-amber-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2.5">
        <IdentificationCard size={18} className="text-amber-500" weight="duotone" />
        <div>
          <p className="font-semibold text-slate-800 text-sm">Complete your check-in</p>
          <p className="text-[11px] text-slate-500">Verify your details and sign the agreement before arrival</p>
        </div>
      </div>
      <div className="px-4 py-4 space-y-3">
        <input className={inputCls} placeholder="Full name (as on your ID)" value={name}
          onChange={e => setName(e.target.value.slice(0, 80))} />
        {needPhone ? (
          <input className={inputCls} placeholder="Phone number" inputMode="tel" value={phone}
            onChange={e => setPhone(e.target.value.replace(/[^\d+\-\s()]/g, "").slice(0, 16))} />
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-emerald-600 px-1">
            <CheckCircle size={14} weight="fill" className="shrink-0" />Phone number already on file with your host
          </p>
        )}
        {needId ? (
          <label className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 cursor-pointer">
            <UploadSimple size={16} className="text-amber-500 shrink-0" />
            <span className="truncate">{idFile ? idFile.name : "Upload govt ID (Aadhaar / DL / Passport)"}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
              onChange={e => setIdFile(e.target.files?.[0] || null)} />
          </label>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-emerald-600 px-1">
            <CheckCircle size={14} weight="fill" className="shrink-0" />Government ID verified at booking
          </p>
        )}

        {needSign && (
          <div className="space-y-2 pt-1">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto">
              <p className="text-[11px] font-semibold text-slate-600 mb-1">Terms &amp; House Rules</p>
              {houseRules && (
                <p className="text-[11px] text-slate-600 whitespace-pre-line mb-2">
                  <span className="font-semibold">House rules:</span> {houseRules}
                </p>
              )}
              <p className="text-[11px] text-slate-600 whitespace-pre-line">{terms}</p>
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 px-1">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5" />
              <span>I have read and agree to the terms and house rules above.</span>
            </label>
            <div>
              <p className="text-[11px] font-semibold text-slate-600 mb-1 px-1">Your signature</p>
              <SignaturePad onChange={setSigCanvas} />
            </div>
          </div>
        )}

        {err && <p className="text-xs text-red-500">{err}</p>}
        <button onClick={submit} disabled={submitting}
          className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold transition">
          {submitting ? "Submitting…" : "Submit & sign check-in"}
        </button>
        <p className="text-[10px] text-slate-400 text-center">Shared only with your host and their caretaker, used for guest verification as required by Indian regulations.</p>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-400 w-24 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-700 font-medium flex-1 break-words">{value}</span>
    </div>
  );
}

export default function GuestGuidebook() {
  const { bookingId } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    // A preview link is host-only, so send the host's login with it.
    let token = null;
    try { token = localStorage.getItem("session_token"); } catch {}
    fetch(`${BACKEND_URL}/api/guide/${bookingId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (!data.guidebook_enabled) {
          setError("This property's guidebook is not available yet.");
        } else {
          setGuide(data);
        }
      })
      .catch(status => setError(status === 404 ? "Booking not found." : status === 401 ? "Log in to HostFolio to preview this guidebook." : "Something went wrong. Please try again."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const copyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-400">Loading your guidebook…</p>
      </div>
    </div>
  );

  if (error || !guide) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center max-w-xs">
        <House size={48} className="text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 text-sm">{error || "Guidebook not found."}</p>
      </div>
    </div>
  );

  const checkinDate = guide.check_in ? new Date(guide.check_in).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : null;
  const checkoutDate = guide.check_out ? new Date(guide.check_out).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : null;
  const tmpl = TEMPLATES[guide.guidebook_template] || TEMPLATES.classic;
  const photos = guide.room_photos?.length > 0 ? guide.room_photos : [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className={`bg-gradient-to-br ${tmpl.hero} text-white px-5 pt-10 pb-8 relative`}>
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <House size={16} weight="fill" className="opacity-70" />
            <span className="text-sm opacity-70 font-medium">HostFolio Guidebook</span>
          </div>
          <h1 className="text-2xl font-bold font-manrope mb-0.5">{guide.property_name}</h1>
          {guide.room_name && (
            <div className="flex items-center gap-1.5 mt-1 mb-1">
              <Door size={13} className="opacity-70" />
              <span className="text-sm opacity-90 font-semibold">{guide.room_name}</span>
            </div>
          )}
          {guide.city && <p className="text-sm opacity-80 flex items-center gap-1"><MapPin size={13} /> {guide.city}</p>}
          {guide.guest_name && <p className="text-sm opacity-90 mt-3">Welcome, <strong>{guide.guest_name}</strong>! 🙏</p>}
        </div>
      </div>

      {/* Stay summary bar */}
      {checkinDate && (
        <div className="bg-white border-b border-slate-200 px-5 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between text-xs">
            <div>
              <p className="text-slate-400">Check-in</p>
              <p className="font-semibold text-slate-700">{checkinDate}{guide.checkin_time ? ` · ${guide.checkin_time}` : ""}</p>
            </div>
            <div className="text-slate-300 text-lg">→</div>
            <div className="text-right">
              <p className="text-slate-400">Check-out</p>
              <p className="font-semibold text-slate-700">{checkoutDate}{guide.checkout_time ? ` · ${guide.checkout_time}` : ""}</p>
            </div>
          </div>
        </div>
      )}

      {/* Room photos carousel */}
      {photos.length > 0 && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="relative rounded-2xl overflow-hidden aspect-video bg-slate-200">
            <img src={photos[photoIdx]} alt="Room" className="w-full h-full object-cover" />
            {photos.length > 1 && (
              <>
                <button onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full text-white flex items-center justify-center text-lg">‹</button>
                <button onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full text-white flex items-center justify-center text-lg">›</button>
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                  {photos.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === photoIdx ? "bg-white" : "bg-white/40"}`} />)}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-5 space-y-3">

        {/* Digital check-in — name, phone, govt ID */}
        {!guide.is_preview && guide.checkin_details && (
          <CheckinForm bookingId={bookingId} guestName={guide.guest_name} details={guide.checkin_details}
            terms={guide.terms_text} houseRules={guide.house_rules} />
        )}

        {/* WiFi */}
        {guide.wifi_name && (
          <Section icon={WifiHigh} title="WiFi">
            <div className="mt-3 space-y-1">
              <InfoRow label="Network" value={guide.wifi_name} />
              <InfoRow label="Password" value={guide.wifi_password} />
            </div>
          </Section>
        )}

        {/* Check-in instructions */}
        {guide.checkin_instructions && (
          <Section icon={Clock} title="Check-in Instructions">
            <p className="text-sm text-slate-600 mt-3 whitespace-pre-line leading-relaxed">{guide.checkin_instructions}</p>
            {guide.checkin_video_url && (
              <a href={guide.checkin_video_url} target="_blank" rel="noreferrer"
                className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl ${tmpl.accentBg} ${tmpl.accentBorder} border text-sm font-semibold ${tmpl.accent}`}>
                <VideoCamera size={15} weight="duotone" />
                Watch check-in video
              </a>
            )}
          </Section>
        )}

        {/* House Rules */}
        {guide.house_rules && (
          <Section icon={Info} title="House Rules">
            <p className="text-sm text-slate-600 mt-3 whitespace-pre-line leading-relaxed">{guide.house_rules}</p>
          </Section>
        )}

        {/* Restaurants */}
        {guide.restaurants_enabled !== false && guide.restaurants?.length > 0 && (
          <Section icon={ForkKnife} title="Places to Eat">
            <div className="mt-3 space-y-3">
              {guide.restaurants.map((r, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                        {r.badge && BADGE_STYLES[r.badge] && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${BADGE_STYLES[r.badge]}`}>{r.badge}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{[r.cuisine, r.price_range].filter(Boolean).join(" · ")}</p>
                    </div>
                    {r.maps_link && (
                      <a href={r.maps_link} target="_blank" rel="noreferrer" className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-lg bg-amber-50 text-amber-600 border border-amber-200">
                        Maps
                      </a>
                    )}
                  </div>
                  {r.host_note && <p className="text-xs text-slate-500 mt-1.5 italic">"{r.host_note}"</p>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Places to visit */}
        {guide.places?.length > 0 && (
          <Section icon={Buildings} title="Nearby Attractions">
            <div className="mt-3 space-y-3">
              {guide.places.map((p, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                        {p.badge && BADGE_STYLES[p.badge] && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${BADGE_STYLES[p.badge]}`}>{p.badge}</span>
                        )}
                      </div>
                      {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                    </div>
                    {p.maps_link && (
                      <a href={p.maps_link} target="_blank" rel="noreferrer" className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-lg bg-amber-50 text-amber-600 border border-amber-200">
                        Maps
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Things to know */}
        {guide.things_to_know?.filter(Boolean).length > 0 && (
          <Section icon={Info} title="Good to Know">
            <ul className="mt-3 space-y-1.5">
              {guide.things_to_know.filter(Boolean).map((tip, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Emergency contacts */}
        {guide.emergency_contacts?.length > 0 && (
          <Section icon={PhoneCall} title="Emergency Contacts">
            <div className="mt-3 space-y-2">
              {guide.emergency_contacts.map((c, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">{c.label}</p>
                    <p className="text-sm font-medium text-slate-700">{c.name || c.label}</p>
                  </div>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="text-sm font-mono font-semibold text-amber-600 hover:text-amber-700">
                      {c.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Share button */}
        <button
          onClick={copyUrl}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
        >
          {copied ? <><CheckCircle size={16} className="text-emerald-500" /> Link copied!</> : <><Copy size={16} /> Share this guidebook</>}
        </button>

        <p className="text-center text-[11px] text-slate-300 pb-4">
          Powered by <a href="https://hostfolio.in" className="text-amber-400 font-semibold">HostFolio</a>
        </p>
      </div>
    </div>
  );
}
